import { useEffect, useState } from 'react';
import { loadPublicSettings } from './publicSettings';

/**
 * Hire Experts CTA library.
 *
 * The sticky mobile bar + the CTA label A/B test are configured in
 * Admin → Settings → Hire CTA. The admin writes the source of truth to
 * `admin_settings` (key `hire_experts`) via the `hire-cta-settings` function,
 * which also mirrors the non-secret values into the publicly-readable
 * `cms_content.public_settings.hire` — that is what every visitor reads here.
 *
 * The A/B test is OFF by default; when off the label is always "Hire Experts"
 * and no variant is assigned or recorded.
 */

const SUPABASE_URL = (import.meta.env.VITE_PUBLIC_SUPABASE_URL || '').replace(/\/+$/, '');
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
const VARIANT_KEY = 'cendien_hire_variant';

export type HireVariant = 'A' | 'B';
export type HireGroup = 'home' | 'services' | 'industries';

export interface HireCtaSettings {
  stickyEnabled: boolean;
  stickyGroups: HireGroup[];
  stickyLabel: string;
  stickyPhone: boolean;
  abEnabled: boolean;
}

export const HIRE_VARIANT_LABELS: Record<HireVariant, string> = {
  A: 'Hire Experts',
  B: 'Request Consultants',
};

export const DEFAULT_HIRE_CTA_SETTINGS: HireCtaSettings = {
  stickyEnabled: true,
  stickyGroups: ['home', 'services', 'industries'],
  stickyLabel: 'Hire Experts',
  stickyPhone: true,
  abEnabled: false,
};

const ALL_GROUPS: HireGroup[] = ['home', 'services', 'industries'];

function normalizeGroups(raw: unknown): HireGroup[] {
  if (!Array.isArray(raw)) return [...DEFAULT_HIRE_CTA_SETTINGS.stickyGroups];
  const picked = raw.map((g) => String(g)).filter((g): g is HireGroup => (ALL_GROUPS as string[]).includes(g));
  return picked.length ? Array.from(new Set(picked)) : [];
}

export function normalizeHireCta(raw: unknown): HireCtaSettings {
  const v = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  return {
    stickyEnabled: v.stickyEnabled !== false,
    stickyGroups: v.stickyGroups === undefined ? [...DEFAULT_HIRE_CTA_SETTINGS.stickyGroups] : normalizeGroups(v.stickyGroups),
    stickyLabel: typeof v.stickyLabel === 'string' && v.stickyLabel.trim() ? v.stickyLabel : DEFAULT_HIRE_CTA_SETTINGS.stickyLabel,
    stickyPhone: v.stickyPhone !== false,
    abEnabled: v.abEnabled === true,
  };
}

// Module-level abEnabled flag so non-hook callers (the form) can decide whether
// to assign a variant without needing the settings object.
let abEnabledFlag = false;
let cachedSettings: HireCtaSettings = DEFAULT_HIRE_CTA_SETTINGS;

/** Loads + caches the public Hire CTA settings (safe to call repeatedly). */
export async function loadHireCtaSettings(): Promise<HireCtaSettings> {
  try {
    const ps = await loadPublicSettings();
    const hire = (ps as unknown as { hire?: unknown }).hire;
    cachedSettings = normalizeHireCta(hire);
    abEnabledFlag = cachedSettings.abEnabled;
    return cachedSettings;
  } catch {
    return cachedSettings;
  }
}

export function getCachedHireCtaSettings(): HireCtaSettings {
  return cachedSettings;
}

/** 50/50 sticky variant per visitor, persisted in localStorage (try/catch). */
export function getStickyHireVariant(): HireVariant {
  if (!abEnabledFlag) return 'A';
  if (typeof window === 'undefined') return 'A';
  try {
    const stored = window.localStorage.getItem(VARIANT_KEY);
    if (stored === 'A' || stored === 'B') return stored;
    const chosen: HireVariant = Math.random() < 0.5 ? 'A' : 'B';
    window.localStorage.setItem(VARIANT_KEY, chosen);
    return chosen;
  } catch {
    return 'A';
  }
}

/** The CTA label for the current variant (always the admin label when A/B is off). */
export function hireCtaLabel(settings: HireCtaSettings, variant: HireVariant): string {
  if (settings.abEnabled) return HIRE_VARIANT_LABELS[variant];
  return settings.stickyLabel || DEFAULT_HIRE_CTA_SETTINGS.stickyLabel;
}

/** Fires a lightweight view/open event for the A/B report (non-blocking). */
export function recordHireCtaEvent(type: 'view' | 'open', variant: HireVariant): void {
  if (!abEnabledFlag || !SUPABASE_URL) return;
  try {
    void fetch(`${SUPABASE_URL}/functions/v1/hire-cta-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ action: 'event', type, variant }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch {
    /* ignore */
  }
}

export interface HireCta {
  settings: HireCtaSettings;
  variant: HireVariant;
  /** The CTA label to render (A/B-aware). */
  label: string;
}

/** React hook — loads the public settings once and exposes the resolved CTA. */
export function useHireCta(): HireCta {
  const [settings, setSettings] = useState<HireCtaSettings>(() => getCachedHireCtaSettings());
  const [variant, setVariant] = useState<HireVariant>('A');

  useEffect(() => {
    let alive = true;
    loadHireCtaSettings().then((s) => {
      if (!alive) return;
      setSettings(s);
      setVariant(getStickyHireVariant());
    });
    return () => { alive = false; };
  }, []);

  return { settings, variant, label: hireCtaLabel(settings, variant) };
}

export { ALL_GROUPS as HIRE_CTA_GROUPS };